import { StandardEvents } from '@shopify/events';

/**
 * Keeps the wholesale threshold counter current on surfaces the cart does not
 * re-render for itself.
 *
 * WHY THIS EXISTS:
 * On the cart page and in the cart drawer, component-cart-items.js and
 * product-form.js both send a `sections` parameter naming every section that
 * contains a cart-items-component, so the counter is re-rendered by Liquid for
 * free and this element is not used there.
 *
 * The product page is NOT in that list. Adding to cart from a PDP re-renders
 * the cart, not the page you are standing on, so a Liquid-only counter shows a
 * stale count until reload. This listens for the cart change and asks Shopify
 * for a fresh copy of its own section.
 *
 * It re-fetches rather than recomputing in JS on purpose: the copy, the three
 * states and the maths stay in one place, in Liquid, driven by the shop
 * metafields. Nothing here knows the threshold or the wording.
 *
 * Usage - only pass section_id where the cart will not re-render for you:
 *   {% render 'wholesale-threshold', section_id: section.id %}
 */
class WholesaleThreshold extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    // Wait for the cart request to settle, otherwise the section still renders
    // the old cart.item_count.
    const settled = event.promise ?? Promise.resolve();

    settled
      .then(() => this.#refresh())
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[wholesale-threshold] cart update rejected:', error);
        }
      });
  };

  async #refresh() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`;

    try {
      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(
        `wholesale-threshold[data-section-id="${CSS.escape(sectionId)}"]`
      );

      // An empty cart renders no counter at all, so a missing element is a
      // valid answer: clear ourselves rather than leaving a stale panel.
      this.innerHTML = fresh ? fresh.innerHTML : '';
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[wholesale-threshold] section refresh failed:', error);
      }
    }
  }
}

if (!customElements.get('wholesale-threshold')) {
  customElements.define('wholesale-threshold', WholesaleThreshold);
}
